import { MUSIC } from '../config.js';

// ---------------------------------------------------------------------------
// The note button, and the two tracks behind it.
//
// Three rules, the first two about not being rude:
//   1. Silence is the default. Sound that starts on its own is the fastest way
//      to make someone close a tab, and browsers block it anyway.
//   2. Nothing is fetched until somebody asks for it, so a visitor who never
//      presses the button never pays for the megabytes.
//   3. The pair are cross-faded by the daylight, never cut between. Both
//      elements play in sync the whole time and only their volumes move, so
//      the day track is where you left it when the sun comes back up.
// ---------------------------------------------------------------------------

const STORE_KEY = 'sofatech:music';

export class Music {
    constructor(root) {
        this.button = root.querySelector('#music-toggle');
        this.tracks = null;
        this.on = false;
        this.level = 0;         // volume of whichever track is up
        this.current = 'day';   // the one that is playing
        this.wanted = 'day';    // the one the sky is asking for
        this.handover = false;  // fading out, about to swap
        if (!this.button) return;

        if (!MUSIC.day && !MUSIC.night) {
            this.button.hidden = true;
            return;
        }

        this.button.addEventListener('click', () => this.toggle());

        try {
            if (localStorage.getItem(STORE_KEY) === 'on') this._armResume();
        } catch { /* storage disabled; not worth caring about */ }
    }

    // Autoplay is blocked until the visitor interacts, so a remembered "on"
    // waits for the first click or keypress rather than failing silently.
    _armResume() {
        const resume = () => {
            window.removeEventListener('pointerdown', resume);
            window.removeEventListener('keydown', resume);
            if (!this.on) this.toggle();
        };
        window.addEventListener('pointerdown', resume, { once: true });
        window.addEventListener('keydown', resume, { once: true });
    }

    /**
     * `?day=moonlight&night=the-long-dark` swaps either track without a
     * rebuild, so a pair can be auditioned against the actual sunset rather
     * than against a list of adjectives. Names only — never an arbitrary URL,
     * which would let any link play any audio from this page.
     */
    _pick(role, fallback) {
        try {
            const name = new URLSearchParams(window.location.search).get(role);
            if (name && /^[a-z0-9-]{1,40}$/.test(name)) return `assets/music/${name}.mp3`;
        } catch { /* no URL, no bother */ }
        return fallback;
    }

    _load() {
        if (this.tracks) return this.tracks;
        const make = (src) => {
            const a = new Audio();
            a.src = src;
            a.loop = true;
            a.preload = 'auto';
            a.volume = 0;
            // Say what went wrong. Swallowing this is why a dead button looks
            // like a dead button and not like a 404 or a MIME type.
            a.addEventListener('error', () => {
                const err = a.error;
                console.warn(`[music] ${src} failed`,
                    err ? `code ${err.code}: ${err.message || '(no message)'}` : '');
            });
            return a;
        };
        this.tracks = {
            day: make(this._pick('day', MUSIC.day)),
            night: make(this._pick('night', MUSIC.night)),
        };
        return this.tracks;
    }

    toggle() {
        this.on = !this.on;
        this.button.setAttribute('aria-pressed', String(this.on));
        this.button.title = this.on ? 'Mute music' : 'Play music';
        this.button.setAttribute('aria-label', this.button.title);
        try {
            localStorage.setItem(STORE_KEY, this.on ? 'on' : 'off');
        } catch { /* ignore */ }

        if (!this.on) return;

        const tracks = this._load();
        this.button.classList.add('is-loading');
        // Only the track for the current sky — the other stays stopped until
        // the sun hands over to it.
        Promise.all([tracks[this.current].play()]).then(() => {
            this.button.classList.remove('is-loading');
        }).catch((err) => {
            // Blocked, missing, or offline: fail back to silence honestly
            // rather than leaving a button that looks as if it worked.
            console.warn(`[music] could not start: ${err?.name}: ${err?.message}`);
            this.button.classList.remove('is-loading');
            this.on = false;
            this.button.setAttribute('aria-pressed', 'false');
            this.button.title = 'Play music';
        });
    }

    /**
     * @param dt          seconds
     * @param nightAmount 0..1 from the environment, the same value that lights
     *                    the lanterns and darkens the water
     */
    update(dt, nightAmount = 0) {
        if (!this.tracks) return;

        // Which track the sky is asking for. The two thresholds are not the
        // same number on purpose: at a single one, a sky hovering right on the
        // line would start a handover every few frames.
        if (nightAmount > 0.55) this.wanted = 'night';
        else if (nightAmount < 0.45) this.wanted = 'day';

        // One track is audible at a time. Rather than blend them, the outgoing
        // one is taken all the way down and stopped before the incoming one is
        // started — dusk is a change of music, not a moment of both.
        if (this.wanted !== this.current) this.handover = true;

        const wantLevel = (this.handover || !this.on) ? 0 : 1;
        // Asked for silence: get out of the way quickly. Handing one track to
        // another at dusk is a different matter and can take its time.
        const rate = this.on ? MUSIC.fade : MUSIC.fadeOut;
        this.level += (wantLevel - this.level) * Math.min(1, dt * rate);

        const active = this.tracks[this.current];
        const other = this.tracks[this.current === 'day' ? 'night' : 'day'];
        other.volume = 0;
        if (!other.paused) other.pause();
        active.volume = Math.max(0, Math.min(1, MUSIC.volume * this.level));

        // Silence reached: hand over, and start the new one from nothing.
        if (this.handover && this.level < 0.02) {
            active.pause();
            this.current = this.wanted;
            this.handover = false;
            if (this.on) {
                const next = this.tracks[this.current];
                next.volume = 0;
                next.currentTime = 0;
                next.play().catch((err) => {
                    console.warn(`[music] handover failed: ${err?.name}: ${err?.message}`);
                });
            }
        }

        if (!this.on && this.level < 0.02 && !active.paused) active.pause();
    }
}
